// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { create } from "@bufbuild/protobuf"
import * as Actions from "../actions"
import * as TimelineSlice from "../slices/timeline"
import { patchDevelopmentUrl } from "../devUrls"
import { showSnackBar } from "../../snackbar/actions"
import { Code, executorMutation, Init, isConnectError, timelineService } from "../../../app/rpc"
import {
  DeclineTagSuggestionRequestSchema,
  DeclineTagSuggestionResponse,
} from "generated/wisetime/user/timeline/v4/timeline_pb"

type DeclineTagSuggestionRpc = (request: Init<typeof DeclineTagSuggestionRequestSchema>) => Observable<DeclineTagSuggestionResponse>

export const declineTagSuggestion = (declineTagSuggestionRpc: DeclineTagSuggestionRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.declineTagSuggestion.match),
      mergeMap(action => declineTagSuggestionRpc(action.payload)
        .pipe(
          map(response => TimelineSlice.updateTimeRowsInCache(
            response.updatedTimeRows.map(row => patchDevelopmentUrl(row))
          )),
          catchError(handleError),
        )
      ),
    )

const handleError = (e: unknown): Observable<Action> => {
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.NotFound) {
      return of(showSnackBar({
        message: t({ id: "msg_decline_tag_suggestion_failed_not_found", message: "Failed to reject suggested tag. Could not find the target time row." }),
        messageType: "ERROR",
      }))
    }
    if (code === Code.PermissionDenied) {
      return of(showSnackBar({
        message: t({ id: "msg_decline_tag_suggestion_failed_permission_denied", message: "Failed to reject suggested tag. You do not have permission to reject a suggested tag to the time row." }),
        messageType: "ERROR",
      }))
    }
    if (code === Code.FailedPrecondition) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_decline_tag_suggestion_failed_precondition", message: "Failed to decline suggested tag. Time row may be out of sync." }),
          messageType: "ERROR"
        })),
        of(Actions.onTimelineOutOfSync())
      )
    }
  }

  return of(showSnackBar({
    message: t({ id: "msg_decline_tag_suggestion_failed", message: "Failed to reject suggested tag. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}

const declineTagSuggestionRpc = (request: Init<typeof DeclineTagSuggestionRequestSchema>): Observable<DeclineTagSuggestionResponse> =>
  executorMutation(() => timelineService.declineTagSuggestion(create(DeclineTagSuggestionRequestSchema, request)))

export default declineTagSuggestion(declineTagSuggestionRpc)
